<div class="conversation">
  <!-- Header: the shopper -->
  <div class="conv-header">
    @if (showBack) {
      <button mat-icon-button type="button" aria-label="Back to messages" (click)="back.emit()">
        <mat-icon>arrow_back</mat-icon>
      </button>
    }
    <fh-chat-image class="avatar" [path]="header()?.buyerPicture" [alt]="header()?.buyerName ?? ''" />
    <div class="conv-title">
      <span class="name">{{ header()?.buyerName ?? (loading() ? "Loading…" : "") }}</span>
      @if (header()?.iso2) {
        <span class="sub">Shopper · {{ header()!.iso2.toUpperCase() }}</span>
      }
    </div>
    <button mat-icon-button type="button" aria-label="More options" [matMenuTriggerFor]="convMenu">
      <mat-icon>more_vert</mat-icon>
    </button>
    <mat-menu #convMenu="matMenu" xPosition="before">
      <button mat-menu-item type="button" (click)="openSettings.emit()">
        <mat-icon>tune</mat-icon>
        <span>Message settings</span>
      </button>
      <button mat-menu-item type="button" [disabled]="!header()" (click)="deleteConversation()">
        <mat-icon>delete_outline</mat-icon>
        <span>Delete conversation</span>
      </button>
    </mat-menu>
  </div>

  <!-- Pinned on every conversation -->
  <div class="safety-banner">
    <mat-icon>verified_user</mat-icon>
    <span>
      Pay only through FishyHub - dead-on-arrival protection and refunds only cover FishyHub
      orders. Contact details and outside links can't be sent.
    </span>
  </div>

  <!-- Messages -->
  <div class="messages" #scroller>
    @if (loading()) {
      <div class="center-state"><mat-spinner diameter="32"></mat-spinner></div>
    } @else if (loadError()) {
      <div class="center-state">
        <p>{{ loadError() }}</p>
        <button mat-stroked-button type="button" (click)="load()">Try again</button>
      </div>
    } @else {
      @if (hasEarlier()) {
        <div class="load-earlier">
          <button mat-stroked-button type="button" [disabled]="loadingEarlier()" (click)="loadEarlier()">
            {{ loadingEarlier() ? "Loading…" : "Load earlier messages" }}
          </button>
        </div>
      }

      @for (item of timeline(); track item.key) {
        @if (item.type === "day") {
          <div class="day-separator"><span>{{ item.label }}</span></div>
        } @else {
          <div
            class="msg-row"
            [class.own]="item.message.senderRole === 'vendor'"
            [class.buyer]="item.message.senderRole === 'buyer'"
            [class.admin]="item.message.senderRole === 'admin'"
            [class.system]="item.message.senderRole === 'system'"
          >
            <div class="bubble" [class.product-bubble]="item.message.kind === 'product'">
              @if (item.message.senderRole === "admin") {
                <div class="role-label">
                  <mat-icon>verified_user</mat-icon>
                  FishyHub
                </div>
              } @else if (item.message.senderRole === "system") {
                <div class="role-label">
                  <mat-icon>schedule_send</mat-icon>
                  Automatic reply
                </div>
              }

              <!-- Photos: thumbnails, the full photo on tap -->
              @if (item.message.kind === "image" && item.message.media?.length) {
                <div class="photos" [class.multi]="item.message.media!.length > 1">
                  @for (media of item.message.media!; track $index) {
                    <!-- Only chat/... storage keys are shown (mediaUrl is "" otherwise) -->
                    @if (mediaUrl(media.thumbPath || media.path); as thumb) {
                      <a
                        class="photo"
                        [attr.href]="mediaUrl(media.path) || null"
                        target="_blank"
                        rel="noopener"
                        [style.aspect-ratio]="item.message.media!.length > 1 ? '1 / 1' : aspectRatio(media)"
                      >
                        <img [src]="thumb" alt="Photo" loading="lazy" />
                      </a>
                    }
                  }
                </div>
              }

              <!-- Video: poster with a play button; loaded only on tap -->
              @if (item.message.kind === "video" && item.message.media?.length && mediaUrl(item.message.media![0].path)) {
                @if (item.message.media![0].status === "processing") {
                  <div class="video-state">
                    <mat-spinner diameter="20"></mat-spinner>
                    Video processing…
                  </div>
                } @else if (item.message.media![0].status === "failed") {
                  <div class="video-state failed">
                    <mat-icon>error_outline</mat-icon>
                    Video could not be processed
                  </div>
                } @else if (playing().has(videoKey(item.message, 0))) {
                  <video
                    class="video"
                    controls
                    autoplay
                    playsinline
                    preload="none"
                    [src]="mediaUrl(item.message.media![0].path)"
                    [poster]="mediaUrl(item.message.media![0].posterPath)"
                  ></video>
                } @else {
                  <button
                    type="button"
                    class="video-poster"
                    aria-label="Play video"
                    [style.aspect-ratio]="aspectRatio(item.message.media![0])"
                    (click)="play(videoKey(item.message, 0))"
                  >
                    @if (mediaUrl(item.message.media![0].posterPath); as poster) {
                      <img [src]="poster" alt="Video" loading="lazy" />
                    }
                    <span class="play"><mat-icon>play_arrow</mat-icon></span>
                    @if (item.message.media![0].durationSec) {
                      <span class="duration">0:{{ item.message.media![0].durationSec < 10 ? "0" : "" }}{{ item.message.media![0].durationSec }}</span>
                    }
                  </button>
                }
              }

              <!-- Product card -->
              @if (item.message.kind === "product") {
                <div class="product-card">
                  <fh-chat-image
                    class="product-image"
                    [path]="item.message.productImage"
                    fallbackIcon="image"
                    [alt]="item.message.productName ?? ''"
                  />
                  <div class="product-info">
                    <span class="product-name">{{ item.message.productName ?? "Product" }}</span>
                    @if (item.message.productAvailable === false) {
                      <span class="out-of-stock">Out of stock</span>
                    }
                    @if (item.message.productId) {
                      <a class="product-link" [href]="productUrl(item.message)" target="_blank" rel="noopener">
                        View product
                        <mat-icon>open_in_new</mat-icon>
                      </a>
                    }
                  </div>
                </div>
              }

              @if (item.message.body) {
                <div class="text">{{ item.message.body }}</div>
              }
              <div class="time">{{ timeLabel(item.message.createdAt) }}</div>
            </div>
            @if (item.seen) {
              <div class="seen">Seen</div>
            }
          </div>
        }
      } @empty {
        <div class="center-state"><p>No messages yet.</p></div>
      }
    }
  </div>

  <!-- Composer -->
  @if (closedReason()) {
    <div class="closed-notice">
      <mat-icon>lock</mat-icon>
      {{ closedReason() }}
    </div>
  } @else if (header()) {
    <div class="composer-area">
      @if (quickReplies.length) {
        <div class="quick-replies">
          @for (reply of quickReplies; track $index) {
            <button type="button" class="quick-reply" [matTooltip]="reply" (click)="insertQuickReply(reply)">
              {{ reply }}
            </button>
          }
        </div>
      }

      <div class="composer">
        <button
          mat-icon-button
          type="button"
          aria-label="Attach photos or a video"
          matTooltip="Photos (up to {{ maxImages }}) or a video"
          [disabled]="busy()"
          (click)="fileInput.click()"
        >
          <mat-icon>add_photo_alternate</mat-icon>
        </button>
        <input
          #fileInput
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,image/*,video/mp4,video/quicktime,video/webm"
          multiple
          hidden
          (change)="onFilesSelected($event)"
        />
        <button
          mat-icon-button
          type="button"
          aria-label="Send a product"
          matTooltip="Send a product"
          [disabled]="busy()"
          (click)="openProductPicker()"
        >
          <mat-icon>sell</mat-icon>
        </button>
        <textarea
          #composer
          class="composer-input"
          rows="1"
          placeholder="Write a message"
          aria-label="Message"
          [maxLength]="maxLength"
          [value]="draft()"
          (input)="onDraftInput($event)"
          (keydown)="onComposerKeydown($event)"
        ></textarea>
        <button
          mat-icon-button
          color="primary"
          type="button"
          aria-label="Send"
          [disabled]="!canSend()"
          (click)="send()"
        >
          @if (sending() && !uploading()) {
            <mat-spinner diameter="20"></mat-spinner>
          } @else {
            <mat-icon>send</mat-icon>
          }
        </button>
      </div>

      @if (draft().length > maxLength - 100) {
        <div class="counter">{{ draft().length }} / {{ maxLength }}</div>
      }
      @if (uploading()) {
        <div class="composer-note">
          <mat-spinner diameter="16"></mat-spinner>
          {{ uploading() }}
        </div>
      }
      @if (contactWarning()) {
        <div class="composer-warning">
          <mat-icon>warning_amber</mat-icon>
          {{ contactWarning() }}
        </div>
      }
      @if (blockedWarning()) {
        <div class="composer-warning blocked">
          <mat-icon>block</mat-icon>
          {{ blockedWarning() }}
        </div>
      }
      @if (sendError()) {
        <div class="composer-error">{{ sendError() }}</div>
      }
    </div>
  }
</div>
